import { createAsyncThunk } from "@reduxjs/toolkit";

import { ajax } from "../../../utils/ajax";
import { ICustomerSummary } from "../models";

interface ICustomerSearchParams {
    page?: number;
    text?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    username?: string;
}

interface ISearchCustomersArgs {
    text: string;
    page: number;
}

interface ISearchCustomersResult {
    total: number;
    page: number;
    hasNext: boolean;
    hasPrev: boolean;
    customers: ICustomerSummary[];
}

/**
 * Searches for customers using flexible field-specific or general text search.
 *
 * Supports both general text search and field-specific queries using the format:
 * - `field_name:value` (e.g., "email:sarah@example.com", "first_name:John")
 * - Multiple field filters can be combined with remaining text as general search
 * - Supported fields: first_name, last_name, email, username
 *
 * Results are paginated and include metadata about total count and available pages.
 *
 * @param args - Search arguments
 * @param args.text - Search query string (can include field-specific filters)
 * @param args.page - Page number for pagination (1-based)
 * @returns Promise resolving to search results with pagination metadata
 *
 * @example
 * // General search
 * dispatch(searchCustomers({ text: "Sarah", page: 1 }))
 *
 * @example
 * // Field-specific search
 * dispatch(searchCustomers({ text: "email:sarah@example.com", page: 1 }))
 *
 * @example
 * // Combined search
 * dispatch(searchCustomers({ text: "first_name:Sarah last_name:Jones", page: 1 }))
 */
export const searchCustomers = createAsyncThunk(
    "csr/searchCustomers",
    async (args: ISearchCustomersArgs): Promise<ISearchCustomersResult> => {
        const { text, page } = args;
        const query: ICustomerSearchParams = {
            page: page || 1,
        };
        // Try to tokenize the query string into various fields. Then fallback the rest of the query into the generic `text` field.
        const fields = [
            /(first_name):([^\s]+)/,
            /(last_name):([^\s]+)/,
            /(email):([^\s]+)/,
            /(username):([^\s]+)/,
        ];
        let textMutable = text;
        fields.forEach((pattern) => {
            const match = textMutable.match(pattern);
            if (!match) {
                return;
            }
            const field = match[1] as
                | "first_name"
                | "last_name"
                | "email"
                | "username";
            const value = match[2];
            query[field] = value;
            textMutable = textMutable.replace(match[0], "");
        });
        if (textMutable) {
            query.text = textMutable.trim();
        }
        const resp = await ajax
            .get("/api/csr/users/search/")
            .set("Accept", "application/json")
            .query(query);
        return {
            total: resp.body.count,
            page: query.page || 1,
            hasNext: !!resp.body.next,
            hasPrev: !!resp.body.previous,
            customers: resp.body.results,
        };
    },
);
